import type { Ref } from 'vue';
import { computed } from 'vue';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Field } from '../types/field';
import {
  getSubtotalTax,
  sortExtendedSummariesByColumnOrder,
} from '../helpers/pricing-table/pricing-table-price-summary.helper';
import { PricingTableHelper } from '../helpers/pricing-table.helper';
import type { PricingTable } from '../types/models';
import type { EditorProductProperty } from '../types/editor-product-property';
import type {
  ColumnUnitType,
  ExtendedSummary,
  PricingTableColumn,
  PricingTableSection,
} from '../..';
import { EPricingTableColumn, usePricingTableRows } from '../..';
import { usePricingTableColumns } from './pricing-table-columns.composable';

export function usePricingTableSectionSummary(
  pricingTable: Ref<PricingTable>,
  sectionId: Ref<string>,
  fields: Ref<Field[]>,
  productProperties: Ref<EditorProductProperty[]>
) {
  const section = computed(() =>
    pricingTable.value.pricingTableSections.find(({ id }) => id === sectionId.value)
  );

  const isPreCalculated = computed(() => pricingTable.value.preCalculated);

  const {
    discountColumn,
    totalPriceColumn,
    taxColumn,
    getColumnName,
    getColumnById,
    getColumnDisplayName,
    enabledColumns,
    isColumnEnabled,
  } = usePricingTableColumns(section, isPreCalculated, productProperties);

  const { rows } = usePricingTableRows(section);

  const sectionSummary = computed(() =>
    PricingTableHelper.getSectionSummary(
      sortExtendedSummariesByColumnOrder(section.value),
      fields.value,
      pricingTable.value.preCalculated
    )
  );

  const enabledSummaries = computed(() =>
    sectionSummary.value?.extendedSummaries.filter(summary => {
      if (summary.columnId) {
        const column = enabledColumns.value.find(({ id }) => id === summary.columnId);
        return summary.enabled && column && isColumnEnabled(column);
      }
      return summary.enabled;
    })
  );

  const hasValue = (value: string) => !!value && value !== '0';

  const showDiscount = computed(
    () =>
      discountColumn.value.enabled &&
      totalPriceColumn.value.enabled &&
      (isPreCalculated.value || hasValue(sectionSummary.value.discount?.value))
  );

  const showTax = computed(
    () =>
      taxColumn.value.enabled &&
      (isPreCalculated.value || hasValue(sectionSummary.value.tax?.value))
  );

  const discountValueText = computed(() =>
    hasValue(sectionSummary.value.discount.value)
      ? `-${PricingTableHelper.currencyFormat(sectionSummary.value.discount.value, pricingTable.value.currencySettings)}`
      : t('add-discount')
  );

  const getCurrenyFormattedValue = (value: string) =>
    PricingTableHelper.currencyFormat(value, pricingTable.value.currencySettings);

  const taxValueText = computed(() =>
    getCurrenyFormattedValue(getSubtotalTax(sectionSummary.value))
  );

  const subTotalValueText = computed(() =>
    PricingTableHelper.currencyFormat(
      sectionSummary.value.price.value,
      pricingTable.value.currencySettings
    )
  );

  const isColumnEligableForExtendedSummary = (column: PricingTableColumn) =>
    !['discount', 'tax'].includes(getColumnName(column));

  const isColumnSummaryEnabled = (column: PricingTableColumn) => {
    if (isColumnEligableForExtendedSummary(column)) {
      const extendedSummaryValue = sectionSummary.value.extendedSummaries?.find(
        ({ columnId }) => columnId === column.id
      );

      return extendedSummaryValue?.enabled;
    }

    switch (column.name) {
      case EPricingTableColumn.Discount:
        return sectionSummary.value.discount.enabled;
      case EPricingTableColumn.Tax:
        return sectionSummary.value.tax.enabled;

      default:
        return false;
    }
  };

  const getColumnSummaryUnit = (column: PricingTableColumn): ColumnUnitType => {
    switch (column.name) {
      case EPricingTableColumn.Price:
      case EPricingTableColumn.NetPrice:
      case EPricingTableColumn.TotalPrice:
        return 'Currency';
      case EPricingTableColumn.Units:
        return 'Unknown';
      default:
        return column.unit || 'Unknown';
    }
  };

  const getSummaryValueText = (summary: ExtendedSummary) => {
    const value = summary.value || '0';

    switch (summary.unit) {
      case 'Currency':
        return getCurrenyFormattedValue(value);
      case 'Percentage':
        return `${value}%`;
      default:
        return value;
    }
  };

  const getSummaryDisplayName = ({ displayName, columnId }: ExtendedSummary) => {
    const hasDisplayName = ![undefined, null].includes(displayName);
    return hasDisplayName ? displayName : getColumnDisplayName(getColumnById(columnId));
  };

  const isTaxGroupingEnabled = computed(() => sectionSummary.value.enableTaxGrouping);
  const showTaxGrouping = computed(() => isTaxGroupingEnabled.value && !isPreCalculated.value);

  const groupedTaxes = computed(() => {
    const rowsFromInputFieldValues = PricingTableHelper.getRowsFromInputFieldValues(
      PricingTableHelper.getEnabledRows(rows.value, fields.value),
      enabledColumns.value,
      fields.value
    );
    const enabledRows = PricingTableHelper.getEnabledRows(rowsFromInputFieldValues, fields.value);
    return PricingTableHelper.getGroupedTaxes(enabledRows, section.value.columns);
  });

  const taxColumnDisplayName = computed(() => getColumnDisplayName(taxColumn.value));

  const getTaxGroupName = (taxRate: string, isFlatFee: boolean) => {
    const formattedTaxRate = isFlatFee ? getCurrenyFormattedValue(taxRate) : `${taxRate}%`;
    return `${taxColumnDisplayName.value} (${formattedTaxRate})`;
  };

  const toggleTaxGrouping = (value: boolean): PricingTableSection => ({
    ...section.value,
    sectionSummary: {
      ...sectionSummary.value,
      enableTaxGrouping: value,
    },
  });

  const toggleColumnExtendedSummary = (column: PricingTableColumn): PricingTableSection => {
    const extendedSummaries = sectionSummary.value.extendedSummaries || [];
    const targetSummaryIndex = extendedSummaries.findIndex(entry => entry.columnId === column.id);

    if (targetSummaryIndex !== -1) {
      extendedSummaries[targetSummaryIndex] = {
        ...extendedSummaries[targetSummaryIndex],
        enabled: !extendedSummaries[targetSummaryIndex].enabled,
      };
    } else {
      extendedSummaries.push({
        columnId: column.id,
        name: column.name || '',
        unit: getColumnSummaryUnit(column),
        enabled: true,
      });
    }
    return {
      ...section.value,
      sectionSummary: {
        ...sectionSummary.value,
        extendedSummaries,
      },
    };
  };

  const totalPriceSummary = computed(() =>
    sectionSummary.value.extendedSummaries?.find(
      summary => summary.columnId === totalPriceColumn.value.id
    )
  );

  const showTotalPriceSummary = computed(
    () => totalPriceSummary.value?.enabled && isColumnEnabled(totalPriceColumn.value)
  );

  return {
    sectionSummary,
    enabledSummaries,
    showDiscount,
    showTax,
    discountValueText,
    taxValueText,
    subTotalValueText,
    isColumnSummaryEnabled,
    getColumnSummaryUnit,
    getSummaryValueText,
    getSummaryDisplayName,
    groupedTaxes,
    isTaxGroupingEnabled,
    showTaxGrouping,
    taxColumnDisplayName,
    getCurrenyFormattedValue,
    getTaxGroupName,
    toggleTaxGrouping,
    toggleColumnExtendedSummary,
    isColumnEligableForExtendedSummary,
    totalPriceSummary,
    showTotalPriceSummary,
  };
}
